import { DateTime } from '@ksyos/date-time';
import { Button, FormGroup, Modal, Radio, RadioGroup } from '@ksyos/design-system';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import type { OrganizationTypeWithLinkedProfessions } from '@common/enums/organizationType';
import type { AccountRequestMatch } from '@common/validation/accountRequest';
import type {
    OrganizationEmploymentAddFrontendValidated,
    OrganizationPractitionerAddBackendValidated,
    OrganizationPractitionerAddFrontendValidated,
    PractitionerEmploymentAddValidated,
} from '@common/validation/practitioner';
import type { EmploymentAdd, PractitionerEmployment } from '@frontend/components/elements/AccountCreationModalFields';
import type { ValidationErrors } from '@frontend/utils';
import { MatchingEntityResultCode } from '@common/enums/matchingEntityResultCode';
import { organizationTypesWithRightsToCreateAccountsAndEndEmployments } from '@common/enums/organizationType';
import { Profession } from '@common/enums/profession';
import { ValidationErrorCode } from '@common/enums/validationErrorCode';
import { convertOrganizationTypeToOrganizationTypeWithLinkedProfessions } from '@common/utils/convertOrganizationTypeToOrganizationTypeWithLinkedProfessions';
import { padStartBigCode } from '@common/utils/padStartBigCode';
import {
    organizationEmploymentAddFrontendSchema,
    organizationPractitionerAddFrontendSchema,
} from '@common/validation/practitioner';
import {
    AccountCreationModalFields,
    emptyEmploymentAdd,
    emptyPractitionerAdd,
} from '@frontend/components/elements/AccountCreationModalFields';
import { IndependentDateInput } from '@frontend/components/fields/Date';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import {
    emptyValidationErrors,
    getSpecialismCodeByProfession,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';

interface OrganizationPractitionerAddProps {
    isOpen: boolean;
    onClose: (newPractitionerCreated?: boolean) => void;
    organizationId: string;
}

interface ExtendedEmploymentAdd extends EmploymentAdd {
    isOrganizationManager: boolean | null;
    startDate: string | null;
}

interface AccountAdd extends PractitionerEmployment {
    employment: ExtendedEmploymentAdd;
    matchingResultCode: MatchingEntityResultCode;
}

interface AccountToValidate extends AccountAdd {
    matchingAccountRequestId?: string | null;
    matchingPractitionerHasEmailAddress?: boolean | null;
    practitionerId?: string | null;
    practitionerWillUseCS: boolean | null;
}

const emptyExtendedEmploymentAdd: ExtendedEmploymentAdd = {
    ...emptyEmploymentAdd,
    isOrganizationManager: null,
    startDate: DateTime.now().format('YYYY-MM-DD'),
};

export function OrganizationPractitionerAdd(props: Readonly<OrganizationPractitionerAddProps>) {
    const translate = getSelectedLanguage();
    const abortSignal = useAbortSignal();
    const { frontendSettings } = useFrontendSettings();

    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [willPractitionerUseCS, setWillPractitionerUseCS] = useState<boolean | null>(null);
    const [newAccount, setNewAccount] = useState<AccountAdd>({
        employment: emptyExtendedEmploymentAdd,
        matchingResultCode: MatchingEntityResultCode.NO_MATCH_FOUND,
        practitioner: emptyPractitionerAdd,
    });
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [organizationType, setOrganizationType] = useState<OrganizationTypeWithLinkedProfessions | null>();
    const [modalSize, setModalSize] = useState<'large' | 'xlarge'>('large');
    const [matchingAccountRequestId, setMatchingAccountRequestId] = useState<string | null>(null);
    const [matchingPractitionerHasEmailAddress, setMatchingPractitionerHasEmailAddress] = useState<boolean | null>(
        null
    );
    const [practitionerId, setPractitionerId] = useState<string | null>(null);

    useEffect(() => {
        loadOrganization();
    }, []);

    return (
        <Modal
            isOpen={props.isOpen && organizationType !== undefined}
            onClose={() => props.onClose()}
            size={modalSize}
        >
            <Modal.Header>{translate.accountRequest.addCareProvider}</Modal.Header>
            <Modal.Body>
                {!!organizationType &&
                    organizationTypesWithRightsToCreateAccountsAndEndEmployments.includes(organizationType) && (
                        <FormGroup
                            hasErrors={errors.has('practitionerWillUseCS')}
                            isRequired={true}
                            label={translate.accountRequest.accountType}
                        >
                            <RadioGroup
                                data-automation-test="practitionerWillUseCS"
                                error={getValidationErrorTranslation(errors.get('practitionerWillUseCS'))}
                                name="practitionerWillUseCS"
                                onChange={async (event) => {
                                    setWillPractitionerUseCS(event.target.value === 'true');
                                    validateOne(
                                        'practitionerWillUseCS',
                                        newAccount
                                            ? {
                                                  ...newAccount,
                                                  practitionerWillUseCS: event.target.value === 'true',
                                              }
                                            : undefined
                                    );
                                }}
                                value={willPractitionerUseCS?.toString() ?? ''}
                            >
                                <Radio
                                    key="true"
                                    label={translate.accountRequest.accountTypeEnum['activeUser']}
                                    value="true"
                                />
                                <Radio
                                    key="false"
                                    label={translate.accountRequest.accountTypeEnum['withoutAccess']}
                                    value="false"
                                />
                            </RadioGroup>
                        </FormGroup>
                    )}
                <AccountCreationModalFields
                    errors={errors}
                    handleChangeNewAccount={(
                        newAccountModalFields: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
                    ) => {
                        handleChangeNewAccount(newAccountModalFields);
                    }}
                    handleModalSize={(size: 'large' | 'xlarge') => setModalSize(size)}
                    handleSetExistingId={handleSetExistingId}
                    organizationId={props.organizationId}
                    organizationType={organizationType!}
                    validateMany={(
                        keys: string[],
                        updatedNewAccount?: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
                    ) =>
                        validateMany(
                            keys,
                            updatedNewAccount
                                ? {
                                      ...updatedNewAccount,
                                      employment: {
                                          ...updatedNewAccount.employment,
                                          isOrganizationManager: newAccount.employment.isOrganizationManager,
                                          startDate: newAccount.employment.startDate,
                                      },
                                      practitionerWillUseCS: willPractitionerUseCS,
                                  }
                                : undefined
                        )
                    }
                    validateOne={(
                        key: string,
                        updatedNewAccount?: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
                    ) =>
                        validateOne(
                            key,
                            updatedNewAccount
                                ? {
                                      ...updatedNewAccount,
                                      employment: {
                                          ...updatedNewAccount.employment,
                                          isOrganizationManager: newAccount.employment.isOrganizationManager,
                                          startDate: newAccount.employment.startDate,
                                      },
                                      practitionerWillUseCS: willPractitionerUseCS,
                                  }
                                : undefined
                        )
                    }
                    willPractitionerUseCS={willPractitionerUseCS}
                />
                {frontendSettings.organizationPayoutManagerEnabled && (
                    <FormGroup
                        hasErrors={errors.has('employment.isOrganizationManager')}
                        isRequired={true}
                        label={translate.employment.organizationManager}
                        tooltipText={translate.employment.organizationManagerExplanation}
                    >
                        <RadioGroup
                            data-automation-test="isOrganizationManager"
                            error={getValidationErrorTranslation(errors.get('employment.isOrganizationManager'))}
                            name="employment.isOrganizationManager"
                            onChange={(event) => {
                                handleChangeEmployment({ isOrganizationManager: event.target.value === 'true' });
                                validateOne('employment.isOrganizationManager', {
                                    ...newAccount,
                                    employment: {
                                        ...newAccount.employment,
                                        isOrganizationManager: event.target.value === 'true',
                                    },
                                    practitionerWillUseCS: willPractitionerUseCS,
                                });
                            }}
                            value={newAccount.employment.isOrganizationManager?.toString() ?? ''}
                        >
                            <Radio
                                key="true"
                                label={translate.common.yes}
                                value="true"
                            />
                            <Radio
                                key="false"
                                label={translate.common.no}
                                value="false"
                            />
                        </RadioGroup>
                    </FormGroup>
                )}
                <FormGroup
                    hasErrors={errors.has('employment.startDate')}
                    isRequired
                    label={translate.common.startingDate}
                >
                    <IndependentDateInput
                        data-automation-test="startDate"
                        error={errors.get('employment.startDate')}
                        hasMaxWidth={true}
                        minDate={DateTime.now().format()}
                        onChange={(value) => handleChangeEmployment({ startDate: value })}
                        onDoValidate={(value) =>
                            validateOne('employment.startDate', {
                                ...newAccount,
                                employment: {
                                    ...newAccount.employment,
                                    startDate: value,
                                },
                                practitionerWillUseCS: willPractitionerUseCS,
                            })
                        }
                        placeholder={translate.common.datePlaceholder}
                        size="large"
                        value={newAccount.employment.startDate}
                        variant="outline"
                    />
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    data-key={'cancel'}
                    onClick={() => props.onClose()}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
                <Button
                    data-key={'submit'}
                    onClick={async () => {
                        await handleSubmit();
                    }}
                >
                    {translate.common.addTo}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function validateOne(key: string, updatedNewAccount?: AccountToValidate) {
        const validationErrors = updateValidationErrorsForOne(
            organizationPractitionerAddFrontendSchema,
            updatedNewAccount
                ? { ...updatedNewAccount, organizationType }
                : {
                      ...newAccount,
                      organizationType,
                      practitionerWillUseCS: willPractitionerUseCS,
                  },
            errors,
            key
        );
        setErrors(validationErrors);
    }

    function validateMany(keys: string[], updatedNewAccount?: AccountToValidate) {
        const validationErrors = updateValidationErrorsForMany(
            organizationPractitionerAddFrontendSchema,
            updatedNewAccount
                ? { ...updatedNewAccount, organizationType }
                : {
                      ...newAccount,
                      organizationType,
                      practitionerWillUseCS: willPractitionerUseCS,
                  },
            errors,
            keys
        );
        setErrors(validationErrors);
    }

    function handleChangeEmployment(employmentUpdate: Partial<ExtendedEmploymentAdd>) {
        setNewAccount({
            ...newAccount,
            employment: { ...newAccount.employment, ...employmentUpdate },
        });
    }

    function handleChangeNewAccount(
        accountUpdate: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
    ) {
        const newAccountUpdate = {
            ...accountUpdate,
            employment: {
                ...accountUpdate.employment,
                isOrganizationManager: newAccount.employment.isOrganizationManager,
                startDate: newAccount.employment.startDate,
            },
        };
        if (
            newAccountUpdate.employment.profession &&
            Object.values(Profession).includes(newAccountUpdate.employment.profession) &&
            newAccountUpdate.employment.profession !== newAccount.employment.profession
        ) {
            setErrors(emptyValidationErrors);
        }

        setNewAccount(newAccountUpdate);
    }

    async function handleSubmit() {
        if (!isSaving) {
            setIsSaving(true);

            const { errors: validationErrors, item } = practitionerId
                ? validate(organizationEmploymentAddFrontendSchema, {
                      employment: {
                          ...newAccount.employment,
                          emailAddress: newAccount.practitioner.emailAddress,
                          specialismCode: getSpecialismCodeByProfession(
                              newAccount.employment.profession,
                              newAccount.employment.specialismCode ?? null
                          ),
                      },
                      matchingAccountRequestId,
                      matchingPractitionerHasEmailAddress,
                      matchingResultCode: newAccount.matchingResultCode,
                      organizationId: props.organizationId,
                      organizationType: organizationType,
                      practitionerId,
                      practitionerRegistrationCodes: {
                          identifierDeclaration: newAccount.practitioner.identifierDeclaration,
                          identifierRegistrationBig: newAccount.practitioner.identifierRegistrationBig,
                          identifierRegistrationKp: newAccount.practitioner.identifierRegistrationKp,
                      },
                      practitionerWillUseCS: willPractitionerUseCS,
                  })
                : validate(organizationPractitionerAddFrontendSchema, {
                      ...newAccount,
                      employment: {
                          ...newAccount.employment,
                          specialismCode: getSpecialismCodeByProfession(
                              newAccount.employment.profession,
                              newAccount.employment.specialismCode ?? null
                          ),
                      },
                      matchingAccountRequestId,
                      matchingPractitionerHasEmailAddress,
                      organizationType: organizationType,
                      practitionerWillUseCS: willPractitionerUseCS,
                  });

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                const itemToSubmit: PractitionerEmploymentAddValidated | OrganizationPractitionerAddBackendValidated =
                    formatItemToSubmit(item!);

                const result = await fetchJson({
                    allowedErrorCodes: [
                        ValidationErrorCode.USERNAME_EXISTS,
                        ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS,
                        ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS,
                        ValidationErrorCode.IDENTIFIER_REGISTRATION_KP_EXISTS,
                        ValidationErrorCode.PRACTITIONER_HAS_KSYOS_EMPLOYMENT,
                    ],
                    body: itemToSubmit,
                    method: 'POST',
                    signal: abortSignal,
                    url: practitionerId
                        ? `/api/practitioners/${practitionerId}/employments?isExistingPractitioner=true`
                        : `/api/organizations/${props.organizationId}/practitioner`,
                });
                handleValidationErrorCode(result.errorCode);
            }

            setIsSaving(false);
        }
    }

    async function loadOrganization() {
        const organization = await getOneOrganization(props.organizationId, abortSignal);
        const organizationTypeWithLinkedProfessions = convertOrganizationTypeToOrganizationTypeWithLinkedProfessions(
            organization.organizationType!
        );
        setOrganizationType(organizationTypeWithLinkedProfessions);
        if (!organizationTypesWithRightsToCreateAccountsAndEndEmployments.includes(organization.organizationType!)) {
            setWillPractitionerUseCS(true);
        }
    }

    function handleValidationErrorCode(errorCode: string | undefined) {
        if (errorCode === ValidationErrorCode.USERNAME_EXISTS) {
            setErrors(errors.set('userName', errorCode));
        } else if (errorCode === ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS) {
            setErrors(errors.set('practitioner.identifierDeclaration', errorCode));
        } else if (errorCode === ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS) {
            setErrors(errors.set('practitioner.identifierRegistrationBig', errorCode));
        } else if (errorCode === ValidationErrorCode.IDENTIFIER_REGISTRATION_KP_EXISTS) {
            setErrors(errors.set('practitioner.identifierRegistrationKp', errorCode));
        } else {
            setErrors(emptyValidationErrors);
            props.onClose(true);
        }
    }

    function handleSetExistingId(existingId?: AccountRequestMatch): void {
        if (existingId) {
            if (existingId.entity === 'matchingAccountRequestId') {
                setMatchingAccountRequestId(existingId.id);
                setPractitionerId(null);
            } else if (existingId.entity === 'practitionerId') {
                setPractitionerId(existingId.id);
                setMatchingAccountRequestId(null);
            }
            setMatchingPractitionerHasEmailAddress(existingId.hasPractitionerEmailAddress);
        } else {
            setMatchingAccountRequestId(null);
            setPractitionerId(null);
            setMatchingPractitionerHasEmailAddress(null);
        }

        validateOne('matchingAccountRequestId', {
            ...newAccount,
            matchingAccountRequestId,
            matchingPractitionerHasEmailAddress,
            practitionerWillUseCS: willPractitionerUseCS,
        });
        validateOne('practitionerId', { ...newAccount, practitionerId, practitionerWillUseCS: willPractitionerUseCS });
    }

    function isEmploymentCreationNewFrontendValidated(
        obj: OrganizationEmploymentAddFrontendValidated | OrganizationPractitionerAddFrontendValidated
    ): obj is OrganizationEmploymentAddFrontendValidated {
        const item = obj as OrganizationEmploymentAddFrontendValidated;

        return [item.employment, item.organizationId, item.organizationType, item.practitionerRegistrationCodes].every(
            (field) => field !== undefined
        );
    }

    function isPractitionerEmploymentCreationNewAddValidated(
        obj: OrganizationEmploymentAddFrontendValidated | OrganizationPractitionerAddFrontendValidated
    ): obj is OrganizationPractitionerAddFrontendValidated {
        const item = obj as OrganizationPractitionerAddFrontendValidated;

        return [
            item.employment,
            item.matchingAccountRequestId,
            item.organizationType,
            item.practitioner,
            item.practitionerWillUseCS,
        ].every((field) => field !== undefined);
    }

    function formatItemToSubmit(
        item: OrganizationEmploymentAddFrontendValidated | OrganizationPractitionerAddFrontendValidated
    ): PractitionerEmploymentAddValidated | OrganizationPractitionerAddBackendValidated {
        const clonedItem = cloneDeep(item);

        if (isEmploymentCreationNewFrontendValidated(clonedItem)) {
            clonedItem.practitionerRegistrationCodes.identifierRegistrationBig = padStartBigCode(
                clonedItem.practitionerRegistrationCodes?.identifierRegistrationBig
            ) as string | null;
        }
        if (isPractitionerEmploymentCreationNewAddValidated(clonedItem)) {
            clonedItem.practitioner.identifierRegistrationBig = padStartBigCode(
                clonedItem.practitioner.identifierRegistrationBig
            ) as string | null;
        }
        const { matchingResultCode, ...itemToSubmit } = clonedItem;

        return itemToSubmit;
    }
}
